import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "../lib/api";

const STORAGE_KEY = "pw_chat_session";

function getSessionId() {
  let sid = localStorage.getItem(STORAGE_KEY);
  if (!sid) {
    sid = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(STORAGE_KEY, sid);
  }
  return sid;
}

const INTRO = {
  role: "assistant",
  content:
    "Hey there! I'm the Playworld Helper. Looking for merch ideas, sizing help, or shipping info? Just ask!",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INTRO]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getSessionId);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Load prior history once when opened
    (async () => {
      try {
        const res = await fetch(`${API}/chat/history/${sessionId}`, {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages([INTRO, ...data.messages.map((m) => ({ role: m.role, content: m.content }))]);
        }
      } catch (err) {
        console.error("Failed to load chat history:", err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setSending(true);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          const dataLine = lines.find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          const payload = dataLine.slice(6);
          if (payload === "[DONE]") continue;
          // Un-escape newlines
          const chunk = payload.replace(/\\n/g, "\n");
          assistantText += chunk;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: assistantText };
            return copy;
          });
        }
      }
    } catch (err) {
      console.error("Chat stream failed:", err);
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content: "Oops — I couldn't reach the helper. Please try again.",
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 bg-[#FF2E93] text-white border-4 border-black rounded-full w-16 h-16 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] hover:shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all inline-flex items-center justify-center"
          data-testid="chat-open-button"
          aria-label="Open chat"
        >
          <MessageCircle size={28} strokeWidth={3} />
          <span className="absolute -top-2 -right-2 bg-[#FFD500] text-black text-xs font-bold border-2 border-black rounded-full w-6 h-6 inline-flex items-center justify-center animate-bounce">
            <Sparkles size={12} strokeWidth={3} />
          </span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-40 w-[calc(100vw-3rem)] sm:w-96 h-[560px] bg-[#FFFDF0] border-4 border-black rounded-2xl shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] flex flex-col overflow-hidden"
          data-testid="chat-widget"
        >
          <div className="bg-[#FF2E93] text-white p-4 border-b-4 border-black flex items-center justify-between">
            <div>
              <p className="font-display text-lg uppercase leading-none">Playworld Helper</p>
              <p className="text-xs opacity-90 mt-1">Ask me anything about the shop!</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="border-3 border-black rounded-full p-1 bg-white text-black hover:bg-[#FFD500]"
              style={{ borderWidth: 3 }}
              data-testid="chat-close-button"
              aria-label="Close chat"
            >
              <X size={18} strokeWidth={3} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl border-3 border-black text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#00E5FF] rounded-br-sm"
                      : "bg-white rounded-bl-sm"
                  }`}
                  style={{ borderWidth: 3 }}
                  data-testid={`chat-message-${m.role}-${i}`}
                >
                  {m.content || (
                    <span className="inline-flex gap-1">
                      <span className="w-2 h-2 bg-black rounded-full animate-bounce" />
                      <span className="w-2 h-2 bg-black rounded-full animate-bounce [animation-delay:150ms]" />
                      <span className="w-2 h-2 bg-black rounded-full animate-bounce [animation-delay:300ms]" />
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t-4 border-black p-3 bg-white flex items-center gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type your message..."
              className="pw-input flex-1 text-sm"
              disabled={sending}
              data-testid="chat-input"
              aria-label="Message input"
            />
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className="pw-btn pw-btn-sm pw-btn-pink disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="chat-send-button"
              aria-label="Send message"
            >
              <Send size={16} strokeWidth={3} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
